import type { Config } from '../config.ts';
import { config as defaultConfig } from '../config.ts';
import type { AssessmentResult, Outcome, Verdict } from '../types.ts';

/**
 * Procent wymowy zdania = dokładność z oceny (średnia ocen słów wzorca; słowo pominięte liczy się jako 0). Zweryfikowane na raporcie kalibracji:
 * `accuracy` zgadza się ze średnią ocen słów z dokładnością do 1–2 punktów także dla zdań z brakującym słowem, więc dodatkowe mnożenie przez
 * kompletność karałoby brak słowa drugi raz (zdanie „Do you want to [eat]” miało 74 i dostałoby 59).
 * Gwiazdki są liczone z tej samej wartości w `decide`. Procent na ekranie pokazujemy dla prób ocenionych jednoznacznie; przy werdykcie „niejasne”
 * nie (patrz `attemptPercent`: taka liczba mówi tylko, jak pasuje wzorzec, a nie czy dziecko powiedziało to zdanie).
 */
export function sentencePercent(result: Pick<AssessmentResult, 'accuracy'>): number {
  return Math.round(clamp(result.accuracy));
}

const clamp = (n: number): number => Math.min(100, Math.max(0, Number.isFinite(n) ? n : 0));

/**
 * Procent, który wolno zapisać jako wynik próby. Gdy zdanie NIE zostało zaliczone jako „niejasne” (rozpoznawanie nie jest pewne, co padło — np. „fu”
 * dopowiedziane jako „food”), dokładność z oceny nic nie mówi o tym, czy dziecko powiedziało właśnie to zdanie (transkrypt wygląda na dokładny, a Azure
 * ocenia wzorzec łagodnie), więc taka próba nie może podbijać średniej bloku: liczy się najwyżej tyle, ile wynosi próg zaliczenia minus 1.
 * Zaliczona próba (nawet późniejsza) i tak ją przebija, bo zapisujemy najlepszą próbę zdania.
 */
export function attemptPercent(result: Pick<AssessmentResult, 'accuracy'>, verdict: Pick<Verdict, 'reason'>, cfg: Config = defaultConfig): number {
  const p = sentencePercent(result);
  return verdict.reason === 'unclear' ? Math.min(p, cfg.scoring.minAccuracy - 1) : p;
}

/** Wymowa całego bloku: średnia z najlepszych podejść zdań ocenianych przez Azure. Brak takich zdań → null (nie pokazujemy wyniku). */
export function blockPercent(outcomes: readonly Outcome[]): number | null {
  const values = outcomes.filter((o) => o.kind === 'speak' && typeof o.percent === 'number').map((o) => o.percent as number);
  if (values.length === 0) return null;
  return Math.round(values.reduce((a, b) => a + b, 0) / values.length);
}

export type PercentBand = 'great' | 'good' | 'ok' | 'practice';

export function percentBand(percent: number, cfg: Config = defaultConfig): PercentBand {
  if (percent >= cfg.percent.great) return 'great';
  if (percent >= cfg.percent.good) return 'good';
  if (percent >= cfg.scoring.minAccuracy) return 'ok';
  return 'practice';
}

const BAND_PL: Record<PercentBand, string> = {
  great: 'Świetna wymowa! 🌟',
  good: 'Bardzo dobra wymowa!',
  ok: 'Dobra wymowa — jeszcze trochę ćwiczeń i będzie super.',
  practice: 'Ćwiczymy dalej — z każdym razem wychodzi lepiej!',
};

const BAND_EN: Record<PercentBand, string> = {
  great: 'Great pronunciation! 🌟',
  good: 'Very good pronunciation!',
  ok: 'Good pronunciation — a bit more practice and it will be super.',
  practice: 'Keep practicing — it gets better every time!',
};

export const bandPl = (band: PercentBand): string => BAND_PL[band];

/** Komunikat pod procentem w języku uwag bota (EN albo PL). */
export const bandText = (band: PercentBand, lang: 'en' | 'pl'): string => (lang === 'en' ? BAND_EN[band] : BAND_PL[band]);
